import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Theater, Film, Camera, Palette, Mic2, Music2, Radio, Tv } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { motion, useAnimationFrame, useMotionValue, useTransform } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min;
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
};

interface ParallaxCardsProps {
  children: React.ReactNode;
  baseVelocity: number;
  className?: string;
}

const ParallaxCards = ({ children, baseVelocity = 100, className }: ParallaxCardsProps) => {
  const baseX = useMotionValue(0);
  const [repetitions, setRepetitions] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const calculateRepetitions = () => {
      if (containerRef.current && cardsRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const cardsWidth = cardsRef.current.offsetWidth;
        const newRepetitions = Math.ceil(containerWidth / cardsWidth) + 1;
        setRepetitions(newRepetitions);
      }
    };

    calculateRepetitions();
    window.addEventListener("resize", calculateRepetitions);
    return () => window.removeEventListener("resize", calculateRepetitions);
  }, [children]);

  const x = useTransform(baseX, (v) => `${wrap(-100 / repetitions, 0, v)}%`);

  useAnimationFrame((_, delta) => {
    const moveBy = baseVelocity * (delta / 1000);
    baseX.set(baseX.get() + moveBy);
  });

  return (
    <div className="w-full overflow-hidden whitespace-nowrap" ref={containerRef}>
      <motion.div className={cn("inline-flex", className)} style={{ x }}>
        {Array.from({ length: repetitions }).map((_, i) => (
          <div key={i} ref={i === 0 ? cardsRef : null} className="inline-flex">
            {children}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export const Features = () => {
  const { t } = useTranslation();
  const [count, setCount] = useState(100);
  const isMobile = useIsMobile();

  useEffect(() => {
    const interval = setInterval(() => {
      setCount((prevCount) => {
        if (prevCount >= 1000) return 100;
        return prevCount + 50;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: Theater, key: 'theater' },
    { icon: Film, key: 'film' },
    { icon: Camera, key: 'photography' },
    { icon: Palette, key: 'visualArts' },
    { icon: Mic2, key: 'music' },
    { icon: Music2, key: 'dance' },
    { icon: Radio, key: 'voice' },
    { icon: Tv, key: 'commercial' }
  ];

  const renderFeatureCards = () => {
    const cards = features.map(({ icon: Icon, key }) => (
      <Card key={key} className="hover:shadow-lg transition-shadow duration-300 bg-transparent relative z-10 mx-2 min-w-[280px]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {t(`features.${key}.title`)}
          </CardTitle>
          <CardDescription>{t(`features.${key}.description`)}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            {t(`features.${key}.content`)}
          </p>
        </CardContent>
      </Card>
    ));

    return (
      <>
        <ParallaxCards baseVelocity={-2}>
          {cards}
        </ParallaxCards>
        <ParallaxCards baseVelocity={2}>
          {cards}
        </ParallaxCards>
      </>
    );
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <h2 className="text-3xl font-bold text-center mb-12">
        {t('features.title')} <span className="text-[#22C55E]">{count}+</span>
      </h2>
      {renderFeatureCards()}
    </div>
  );
};